import axios from 'axios';
import config from 'config';
import { isEmpty } from 'lodash';

// Handles both full URLs (https://example.com/path) and relative paths (/path)
const matchesBaseUrl = (url: string, baseUrl: string): boolean => {
  // If baseUrl is a full URL (starts with http:// or https://), use startsWith
  if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
    return url.startsWith(baseUrl);
  }
  // If baseUrl is a relative path, check if the URL's pathname includes it
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.startsWith(baseUrl);
  } catch {
    // If URL parsing fails (relative URL), check if the URL string includes the baseUrl
    return url.startsWith(baseUrl) || url.includes(baseUrl);
  }
};
const axiosNetworkDebuggerInterceptor = axios.create();

type HttpBody = string | number | boolean | null | undefined | HttpBody[] | { [key: string]: HttpBody };

type HttpTransactionBodies = {
  requestBody?: HttpBody;
  responseBody?: HttpBody;
};

const safeBodyForLogging = (body: HttpBody): HttpBody | string => {
  const FALLBACK_BODY = 'undefined';

  return !isEmpty(body) ? body : FALLBACK_BODY;
};

export const makeHttpTransactionKey = (method?: string, url?: string): string => {
  return `${method?.toUpperCase() || 'GET'} ${url}`;
};

export function parseRequestBody(data: unknown): HttpBody | undefined {
  switch (true) {
    case data instanceof FormData: {
      const parsed: Record<string, HttpBody> = {};
      (data as FormData).forEach((value, key) => {
        switch (true) {
          case value instanceof File:
            parsed[key] = (value as File).name;
            break;
          case typeof value === 'string':
            try {
              parsed[key] = JSON.parse(value as string);
            } catch {
              parsed[key] = value as HttpBody;
            }
            break;
          default:
            parsed[key] = value as HttpBody;
        }
      });
      return parsed;
    }

    case typeof data === 'string': {
      try {
        return JSON.parse(data as string);
      } catch {
        return data as HttpBody;
      }
    }

    case typeof data === 'object' && data !== null:
      return data as HttpBody;

    default:
      return undefined;
  }
}

const httpTransactionMap = new Map<string, HttpTransactionBodies>();

axiosNetworkDebuggerInterceptor.interceptors.request.use((reqConfig) => {
  const { method, url, data } = reqConfig;

  if (!url || !matchesBaseUrl(url, config.api.BASE_URL)) {
    return reqConfig;
  }

  const httpTransactionKey = makeHttpTransactionKey(method, url);

  const requestBody = parseRequestBody(data);

  const nonEmptyRequestBody = safeBodyForLogging(requestBody);

  httpTransactionMap.set(httpTransactionKey, {
    ...httpTransactionMap.get(httpTransactionKey),
    requestBody: nonEmptyRequestBody,
  });

  return reqConfig;
});

axiosNetworkDebuggerInterceptor.interceptors.response.use((resConfig) => {
  const {
    config: { method, url },
    data,
  } = resConfig;

  if (!url || !matchesBaseUrl(url, config.api.BASE_URL)) {
    return resConfig;
  }

  const httpTransactionKey = makeHttpTransactionKey(method, url);

  const responseBody = typeof data === 'object' ? data : JSON.stringify(data);

  const nonEmptyResponseBody = safeBodyForLogging(responseBody);

  httpTransactionMap.set(httpTransactionKey, {
    ...httpTransactionMap.get(httpTransactionKey),
    responseBody: nonEmptyResponseBody,
  });

  return resConfig;
});

export { axiosNetworkDebuggerInterceptor, httpTransactionMap };
